import {
  CLIENT_TYPE as BRAND_CLIENT_TYPE,
  DEFAULT_CURRENCY as BRAND_DEFAULT_CURRENCY,
  CLIENT_TYPE_HEADER_KEY,
  DEFAULT_LOCALE,
} from "@kupikupi/brand";

/**
 * Backend `lang` header convention — mirrors legacy aps-web protocol.
 * Backend `I18nUtils.getLang(request)` reads this exact string; shorter
 * forms like `"ru"` / `"zh"` / `"en"` do NOT match the lookup and the
 * backend falls back to default (usually English), which surfaces as
 * untranslated source titles on PDP, register error messages in English,
 * etc.
 */
export type BackendLang = "ru_RU" | "zh_CN" | "en_US";

/**
 * Translates Next.js / next-intl locale codes (`"ru"` / `"zh"` / `"en"`)
 * to the backend's expected lang header. Matches `aps-frontendv2`'s
 * `lib/i18n/lang-header.ts` 1:1 so both frontends speak the same protocol.
 */
export function toBackendLang(locale: string | undefined): BackendLang {
  if (locale === "zh" || locale?.startsWith("zh")) return "zh_CN";
  if (locale === "en" || locale?.startsWith("en")) return "en_US";
  return "ru_RU";
}

export interface BuildHeadersInput {
  acceptLanguage?: string;
  authToken?: string;
  clientType?: "PC Web" | "H5" | "App" | "TG MiniApp";
  /** Target display currency for backend price conversion. */
  currency?: "RUB" | "CNY" | "USD";
  /** UI display language — backend wants `ru_RU`/`zh_CN`/`en_US` format.
   *  Use `toBackendLang(locale)` to convert from next-intl locale codes. */
  lang?: BackendLang;
  /** MD5(username + password + last5(timestamp)) — required for /user/register.
   *  Compute via @kupikupi/api `computeSignture()`. */
  signture?: string;
  /** Millisecond epoch as a string — paired with `signture`. */
  timestamp?: string;
  /** Yandex SmartCaptcha solution token — only attached for retries after a
   *  prior call returned ApiError("4601"). Backend reads from `X-Captcha-Token`
   *  in SearchAbuseGuard.captchaTokenIsValid(). */
  captchaToken?: string;
}

const DEFAULT_CURRENCY: NonNullable<BuildHeadersInput["currency"]> = BRAND_DEFAULT_CURRENCY;
const DEFAULT_LANG: BackendLang = toBackendLang(DEFAULT_LOCALE);
const DEFAULT_CLIENT_TYPE = BRAND_CLIENT_TYPE as NonNullable<BuildHeadersInput["clientType"]>;

export function buildHeaders(input: BuildHeadersInput = {}): HeadersInit {
  const h: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    currency: input.currency ?? DEFAULT_CURRENCY,
    lang: input.lang ?? DEFAULT_LANG,
  };
  if (input.acceptLanguage) h["Accept-Language"] = input.acceptLanguage;
  // jeecg backend reads the JWT from `X-Access-Token`, not `Authorization`
  // (see aps-common JwtUtil.java:128, TokenUtils.java:33). No `Bearer` prefix.
  if (input.authToken) h["X-Access-Token"] = input.authToken;
  h[CLIENT_TYPE_HEADER_KEY] = input.clientType ?? DEFAULT_CLIENT_TYPE;
  if (input.signture) h.signture = input.signture;
  if (input.timestamp) h.timestamp = input.timestamp;
  if (input.captchaToken) h["X-Captcha-Token"] = input.captchaToken;
  return h;
}
